import { ApartmentOutlined, DownOutlined, LogoutOutlined } from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import { Button, message, Spin } from 'antd';
import type { MenuProps } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import HeaderDropdown from '../HeaderDropdown';
import {
  isPositionContext,
  loadUserContexts,
  resetContextAndReload,
  switchContextAndReload,
} from '@/modules/subject/userContext/services/contextSwitch';
import { translateOrgStructureName } from '@/modules/subject/userContext/utils/orgStructureLabel';
import type {
  CurrentUserContext,
  UserContextItem,
} from '@/modules/subject/userContext/types/userContext.types';

const RESET_KEY = '__reset_context__';

function contextLabel(
  intl: ReturnType<typeof useIntl>,
  item: {
    organization_name?: string | null;
    org_structure_name?: string | null;
    org_structure_id?: string | null;
  },
): string {
  return (
    translateOrgStructureName(intl, item.org_structure_name) ||
    item.organization_name ||
    `${intl.formatMessage({
      id: 'component.contextSwitcher.fallback',
      defaultMessage: 'Position',
    })} ${item.org_structure_id ?? ''}`.trim()
  );
}

export const ContextSwitcher: React.FC = () => {
  const intl = useIntl();
  const [contexts, setContexts] = useState<UserContextItem[]>([]);
  const [current, setCurrent] = useState<CurrentUserContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const state = await loadUserContexts();
        if (cancelled) return;
        setContexts(state.contexts);
        setCurrent(state.current);
      } catch {
        // Тихо игнорируем — если контекстов нет, переключатель просто не показываем
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const inChildContext = isPositionContext(current);

  const currentLabel = useMemo(() => {
    if (inChildContext && current && (current.org_structure_name || current.organization_name)) {
      return contextLabel(intl, current);
    }
    return intl.formatMessage({
      id: 'component.contextSwitcher.select',
      defaultMessage: 'Select context',
    });
  }, [current, inChildContext, intl]);

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = contexts.map((ctx) => ({
      key: ctx.org_structure_id,
      icon: <ApartmentOutlined />,
      label: contextLabel(intl, ctx),
    }));
    if (inChildContext) {
      items.push({ type: 'divider' as const });
      items.push({
        key: RESET_KEY,
        icon: <LogoutOutlined />,
        label: intl.formatMessage({
          id: 'component.contextSwitcher.reset',
          defaultMessage: 'Return to main profile',
        }),
      });
    }
    return items;
  }, [contexts, inChildContext, intl]);

  const onMenuClick: MenuProps['onClick'] = async ({ key }) => {
    if (switching) return;
    if (key === current?.org_structure_id) return;
    try {
      setSwitching(true);
      if (key === RESET_KEY) {
        await resetContextAndReload();
      } else {
        await switchContextAndReload(key);
      }
    } catch {
      setSwitching(false);
      message.error(
        intl.formatMessage({
          id: 'component.contextSwitcher.switchFailed',
          defaultMessage: 'Failed to switch context',
        }),
      );
    }
  };

  if (loading || switching) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 8px' }}>
        <Spin size="small" />
      </span>
    );
  }

  if (contexts.length === 0 && !inChildContext) {
    return null;
  }

  return (
    <HeaderDropdown
      placement="bottomRight"
      menu={{
        selectedKeys: inChildContext && current?.org_structure_id ? [current.org_structure_id] : [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      <Button
        type="text"
        icon={<ApartmentOutlined />}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          height: 28,
          maxWidth: 240,
          padding: '4px 8px',
          border: 'none',
          boxShadow: 'none',
          borderRadius: 4,
        }}
      >
        <span
          style={{
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {currentLabel}
        </span>
        <DownOutlined style={{ fontSize: 10 }} />
      </Button>
    </HeaderDropdown>
  );
};

export default ContextSwitcher;
